import React, {
  ComponentType,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import JigsawEvent from "../../components/viewer-context/JigsawEvent";
import SimpleObjectComponent from "components/common/SimpleObjectComponent";
import useJigsawEvent from "../../components/viewer-context/useJigsawEvent";
import { findMarkerById, isEmojiMarker } from "../../utils/markers";
import useHighlightApi, { HighlightEvent } from "./useHighlightApi";

const BLANK = "";
const CLICK_EVENTS = [JigsawEvent.NOTE_ICON_CLICK, JigsawEvent.HIGHLIGHT_CLICK];

const CLOSE_EVENTS = [
  JigsawEvent.BOOK_CLICK,
  JigsawEvent.PAGE_SCROLL,
  JigsawEvent.PAGE_RESIZE,
  JigsawEvent.PAGE_TRANSFORM,
];

const SELECTED_TEXT_EVENTS = [
  JigsawEvent.BOOK_SELECTION_MADE,
  JigsawEvent.HIGHLIGHT_CLICK,
];

export interface MenuItemType {
  enabled: boolean;
  label: string;
  onClick: () => void;
}

export interface PopoverProps {
  coords: { x: number; y: number };
  setNoteText: (e: any) => void;
  clickedMarkerId: string;
  syncId: string;
  open: boolean;
  loading: boolean;
  updateNoteText: (text: string) => void;
  highlighters: Array<jigsaw.Marker>;
  menuItems: Array<MenuItemType>;
  updateHighlight: (clickedMarker: jigsaw.Marker, cb?: () => void) => void;
  deleteHighlight: (c: jigsaw.Marker) => void;
  createHighlight: (c: jigsaw.Marker, cb?: () => void) => void;
  setActiveMarker: (c: jigsaw.Marker) => void;
  activeMarker?: jigsaw.Marker;
  closeMenu: () => void;
  noteText: string;
  selectedText: string;
}

export default function withTextSelect<P extends PopoverProps>(
  Component: ComponentType<P>
): ComponentType<Omit<P, keyof PopoverProps>> {
  return function ComponentWithTextSelect(props) {
    const [open, setOpen] = useState(false);
    const [coords, setCoords] = useState({ x: 0, y: 0 });
    const [markers, setMarkers] = useState<Array<jigsaw.Marker>>([]);
    const [clickedHighlight, setClickedHighlight] = useState({
      markerId: BLANK,
      noteText: BLANK,
      syncId: BLANK,
    });
    const [updatedNoteText, updateNoteText] = useState(BLANK);
    const [activeMarker, setActiveMarker] = useState();
    const [loading, setLoading] = useState(false);

    const [selectedText, setSelectedText] = useState(BLANK);
    const wrapperEl = useRef(null);
    const highlightApi = useHighlightApi();

    useEffect(() => {
      window.Jigsaw.User.getMarkers(r => {
        if (r.isSuccess()) {
          setActiveMarker(r.data.markers[0]);
          setMarkers(r.data.markers.filter(m => !isEmojiMarker(m.label)));
        }
      });
    }, []);

    useEffect(() => {
      const addCloseEvent = (e: any) => {
        const node = ReactDOM.findDOMNode(wrapperEl.current) as any;

        if (node && !node.contains(e.target)) {
          setOpen(false);
        }
      };
      window.addEventListener("click", addCloseEvent);
      return () => {
        window.removeEventListener("click", addCloseEvent);
      };
    }, []);

    const highlightCreated = useCallback((evt: any) => {
      setActiveMarker(evt.detail.data.highlight.marker);
      resetMenu(false);
      setLoading(false);
    }, []);

    const highlightUpdated = useCallback((evt: any) => {
      setActiveMarker(evt.detail.data.highlight.marker);
      resetMenu(false);
      setLoading(false);
    }, []);

    const highlightDeleted = useCallback(() => {
      resetMenu(false);
    }, []);

    useEffect(() => {
      document.addEventListener(HighlightEvent.CREATE, highlightCreated);
      document.addEventListener(HighlightEvent.UPDATE, highlightUpdated);
      document.addEventListener(HighlightEvent.DELETE, highlightDeleted);

      return () => {
        document.removeEventListener(HighlightEvent.CREATE, highlightCreated);
        document.removeEventListener(HighlightEvent.UPDATE, highlightUpdated);
        document.removeEventListener(HighlightEvent.DELETE, highlightDeleted);
      };
    }, [highlightCreated, highlightUpdated, highlightDeleted]);

    const createHighlight = (marker: jigsaw.Marker) => {
      setLoading(true);
      highlightApi.highlightCurrentSelection({
        markerId: marker.globalId,
        noteText: updatedNoteText,
      });
    };

    const updateHighlight = (chosenMarker: jigsaw.Marker) => {
      highlightApi.updateHighlight(clickedHighlight.syncId, {
        markerId: chosenMarker.globalId,
        noteText: updatedNoteText || clickedHighlight.noteText,
      });
    };

    const deleteHighlight = () => {
      highlightApi.deleteHighlight(clickedHighlight.syncId);
    };

    const resetMenu = (open: boolean) => {
      setOpen(open);
      setClickedHighlight({
        noteText: BLANK,
        markerId: BLANK,
        syncId: BLANK,
      });
      updateNoteText(BLANK);
    };

    const onTextSelect = useCallback(
      (event: any, data: jigsaw.event.TextSelect) => {
        setCoords({
          x:
            data.eventPosition.left !== undefined
              ? data.eventPosition.left
              : data.selectionRect.left,
          y:
            data.eventPosition.top !== undefined
              ? data.eventPosition.top
              : data.selectionRect.bottom, // Yes, bottom
        });
        resetMenu(true);
      },
      []
    );

    const onHighlightClick = useCallback(
      (event: any, data: jigsaw.event.HighlightClick) => {
        const marker = findMarkerById(markers, data.markerId);
        if (marker && !isEmojiMarker(marker.label)) {
          setCoords({
            x: data.position.left,
            y: data.position.top + data.position.height,
          });
          setClickedHighlight({
            noteText: data.noteText,
            markerId: data.markerId,
            syncId: data.syncId,
          });
          setOpen(true);
        } else {
          setOpen(false);
        }
      },
      [markers]
    );

    const selectedMarker = () => {
      if (clickedHighlight.markerId) {
        return findMarkerById(markers, clickedHighlight.markerId);
      }
      return activeMarker;
    };

    useJigsawEvent(JigsawEvent.TEXT_SELECTION, onTextSelect);
    useJigsawEvent(CLICK_EVENTS, onHighlightClick);

    useJigsawEvent(CLOSE_EVENTS, (event: any) => {
      if (updatedNoteText && !loading) {
        setLoading(true);
        if (!clickedHighlight.markerId && activeMarker) {
          createHighlight(activeMarker);
        } else if (
          clickedHighlight.markerId &&
          clickedHighlight.noteText !== updatedNoteText
        ) {
          const marker = findMarkerById(markers, clickedHighlight.markerId);
          marker && updateHighlight(marker);
        } else {
          resetMenu(false);
        }
      } else if (
        event.type === JigsawEvent.BOOK_SELECTION_CANCELLED &&
        !activeMarker
      ) {
      } else {
        resetMenu(false);
        setLoading(false);
      }
    });

    useJigsawEvent(SELECTED_TEXT_EVENTS, (event: any, data: any) => {
      if (event.type === JigsawEvent.HIGHLIGHT_CLICK) {
        setSelectedText(data.selectedText);
      } else {
        setSelectedText(data);
      }
    });

    return (
      <SimpleObjectComponent ref={wrapperEl}>
        <Component
          {...(props as P)}
          open={open}
          loading={loading}
          coords={coords}
          createHighlight={createHighlight}
          updateHighlight={updateHighlight}
          deleteHighlight={deleteHighlight}
          activeMarker={selectedMarker()}
          setActiveMarker={(c: jigsaw.Marker) => setActiveMarker(c)}
          clickedMarkerId={clickedHighlight.markerId}
          noteText={updatedNoteText || clickedHighlight.noteText}
          closeMenu={() => setOpen(false)}
          highlighters={markers}
          syncId={clickedHighlight.syncId}
          setNoteText={(text: string) => {
            updateNoteText(text);
          }}
          selectedText={selectedText}
        />
      </SimpleObjectComponent>
    );
  };
}
